import { Alert, Button, Group, PasswordInput, Stack, Text, TextInput, Title } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { getHotkeyHandler } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconAlertOctagon, IconCircleCheck, IconEye, IconEyeOff } from '@tabler/icons-react'
import { observer } from 'mobx-react-lite'
import { parse } from 'qs'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearch } from 'wouter'
import { z } from 'zod'

import { useGlobalState } from '@/hooks/useGlobalState'
import * as commonClasses from '@/styles/componentCommons.css'
import { navigateTo } from '@/utils/navigation'

const MIN_PASSWORD_LENGTH = 8
const RESET_CODE_LENGTH = 6
const RESEND_COOLDOWN_TIME = 60

const ResetPasswordSchema = z
  .object({
    code: z.string().length(RESET_CODE_LENGTH, `Code must be ${RESET_CODE_LENGTH} digits`),
    newPassword: z.string().min(MIN_PASSWORD_LENGTH, `Password must be at least ${MIN_PASSWORD_LENGTH} characters`),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine(data => data.newPassword === data.confirmPassword, {
    message: 'Passwords must match',
    path: ['confirmPassword'],
  })

export const ResetPasswordScreen = observer(function ResetPasswordScreen() {
  const searchParamsString = useSearch()
  const searchParams: { email?: string } = parse(searchParamsString)

  const { api } = useGlobalState()
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [cooldownTime, setCooldownTime] = useState(0)

  // Individual code input refs for auto-advance functionality
  const codeInputRefs = useRef<Array<HTMLInputElement | null>>(Array(RESET_CODE_LENGTH).fill(null))
  const [codeDigits, setCodeDigits] = useState<string[]>(Array(RESET_CODE_LENGTH).fill(''))

  useEffect(() => {
    if (!searchParams.email) {
      console.error('[ResetPassword] No email provided, redirecting to forgot password')
      navigateTo('forgotPassword')
    }
  }, [searchParams.email])

  useEffect(() => {
    let timer: NodeJS.Timeout

    if (cooldownTime > 0) {
      timer = setInterval(() => {
        setCooldownTime(time => Math.max(0, time - 1))
      }, 1000)
    }

    return () => clearInterval(timer)
  }, [cooldownTime])

  const form = useForm<z.infer<typeof ResetPasswordSchema>>({
    mode: 'controlled',
    validate: zodResolver(ResetPasswordSchema),
    initialValues: {
      code: '',
      newPassword: '',
      confirmPassword: '',
    },
  })

  const onKeyboardEnter = useCallback(getHotkeyHandler([['Enter', () => void submitForm()]]), [form.values])

  // Handle code input changes with auto-advance
  const handleCodeDigitChange = (value: string, index: number) => {
    // Handle paste events (multiple digits)
    if (value.length > 1) {
      const digits = value.replace(/[^\d]/g, '').split('').slice(0, RESET_CODE_LENGTH)
      const newDigits = [...codeDigits]

      digits.forEach((digit, i) => {
        if (index + i < RESET_CODE_LENGTH) {
          newDigits[index + i] = digit
        }
      })

      setCodeDigits(newDigits)
      form.setFieldValue('code', newDigits.join(''))

      // Focus on the next empty field or last field
      const nextIndex = Math.min(index + digits.length, RESET_CODE_LENGTH - 1)
      codeInputRefs.current[nextIndex]?.focus()

      return
    }

    // Handle single digit input
    if (value && !/^\d$/.test(value)) return

    const newDigits = [...codeDigits]
    newDigits[index] = value
    setCodeDigits(newDigits)
    form.setFieldValue('code', newDigits.join(''))

    // Auto-advance to next field
    if (value && index < RESET_CODE_LENGTH - 1) {
      codeInputRefs.current[index + 1]?.focus()
    }
  }

  const handleCodeKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key === 'Backspace') {
      const newDigits = [...codeDigits]

      // If current field is empty and we're not at the first input, move back
      if (!codeDigits[index] && index > 0) {
        codeInputRefs.current[index - 1]?.focus()
        newDigits[index - 1] = ''
      } else {
        // If current field has a value, just clear it
        newDigits[index] = ''
      }

      setCodeDigits(newDigits)
      form.setFieldValue('code', newDigits.join(''))
    }
  }

  async function submitForm() {
    setSubmitError(null)

    const validations = form.validate()

    if (validations.hasErrors) {
      setSubmitError('Please fix the errors above')
      return
    }

    const { code, newPassword } = form.getValues()

    try {
      setIsLoading(true)
      setSubmitError(null)

      const response = await api.confirmForgotPassword({
        email: searchParams.email!,
        code: parseInt(code, 10),
        newPassword,
      })

      if (response.kind === 'ok') {
        setIsSuccess(true)
        notifications.show({
          title: 'Password Reset Successful!',
          message: response.message,
          color: 'green',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
      } else {
        setSubmitError(response.data?.message ?? 'Failed to reset password. Please try again.')
      }
    } catch (error: any) {
      console.error('[ResetPassword] Error:', error)
      setSubmitError('An error occurred. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  async function resendCode() {
    if (cooldownTime > 0 || isResending) return

    try {
      setIsResending(true)
      setSubmitError(null)
      setCodeDigits(Array(RESET_CODE_LENGTH).fill(''))
      form.setFieldValue('code', '')

      const response = await api.forgotPassword({ email: searchParams.email! })

      if ('detail' in response) {
        setCooldownTime(RESEND_COOLDOWN_TIME)
        notifications.show({
          title: 'Code Resent',
          message: 'A new reset code has been sent to your email.',
          color: 'blue',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
      } else {
        setSubmitError('Failed to resend code. Please try again.')
      }
    } catch (error: any) {
      console.error('[ResetPassword] Resend error:', error)
      setSubmitError('Failed to resend code. Please try again.')
    } finally {
      setIsResending(false)
    }
  }

  const canSubmit = () => {
    const { code, newPassword, confirmPassword } = form.getValues()
    return (
      !isLoading &&
      code.length === RESET_CODE_LENGTH &&
      newPassword.length >= MIN_PASSWORD_LENGTH &&
      newPassword === confirmPassword
    )
  }

  if (isSuccess) {
    return (
      <Stack gap="xxl" maw={768} w="100%">
        <Title order={6} ta="center" fw={500}>
          Password Reset Successful!
        </Title>

        <Stack align="center" gap="md">
          <IconCircleCheck size={48} color="green" />
          <Text ta="center" size="lg">
            Your password has been successfully reset.
          </Text>
          <Text ta="center" c="dimmed" size="sm">
            You can now log in with your new password.
          </Text>
        </Stack>

        <Stack align="center">
          <Button size="lg" onClick={() => navigateTo('login')} fullWidth>
            Back to Login
          </Button>
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack gap="xl" maw={768} w="100%">
      <Title order={6} ta="center" fw={500}>
        Reset Your Password
      </Title>

      <Text ta="center" c="dimmed" size="sm">
        We sent a code to <strong>{searchParams.email}</strong>
      </Text>

      {submitError && <Alert variant="light" color="red" title={submitError} icon={<IconAlertOctagon />} />}

      {/* Reset Code Inputs */}
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          Enter verification code
        </Text>
        <Group gap="xs" justify="center">
          {Array.from({ length: RESET_CODE_LENGTH }).map((_, index) => (
            <TextInput
              key={index}
              ref={el => {
                codeInputRefs.current[index] = el
              }}
              size="lg"
              w={50}
              maw={50}
              ta="center"
              maxLength={RESET_CODE_LENGTH}
              value={codeDigits[index]}
              onChange={e => handleCodeDigitChange(e.target.value, index)}
              onKeyDown={e => handleCodeKeyDown(e, index)}
              error={form.errors.code && index === 0}
              autoFocus={index === 0}
              styles={{
                input: {
                  textAlign: 'center',
                  fontSize: '1.2rem',
                  fontWeight: 600,
                },
              }}
            />
          ))}
        </Group>
        {form.errors.code && (
          <Text size="xs" c="red" ta="center">
            {form.errors.code}
          </Text>
        )}
      </Stack>

      {/* Password Inputs */}
      <PasswordInput
        label="New Password"
        placeholder="Enter your new password"
        onKeyDown={onKeyboardEnter}
        {...form.getInputProps('newPassword')}
        visibilityToggleIcon={({ reveal }) => (reveal ? <IconEyeOff size={18} /> : <IconEye size={18} />)}
      />

      <PasswordInput
        label="Confirm New Password"
        placeholder="Confirm your new password"
        onKeyDown={onKeyboardEnter}
        {...form.getInputProps('confirmPassword')}
        visibilityToggleIcon={({ reveal }) => (reveal ? <IconEyeOff size={18} /> : <IconEye size={18} />)}
      />

      <Stack align="center" gap="md">
        <Button size="lg" onClick={() => void submitForm()} fullWidth disabled={!canSubmit()} loading={isLoading}>
          {isLoading ? 'Resetting password...' : 'Reset Password'}
        </Button>

        <Group gap="xs">
          <Text size="sm" c="dimmed">
            Didn&apos;t receive the code?
          </Text>
          <Button
            variant="subtle"
            size="compact-sm"
            onClick={() => void resendCode()}
            disabled={cooldownTime > 0 || isResending}
            loading={isResending}>
            {cooldownTime > 0 ? `Resend in ${cooldownTime}s` : 'Click to resend'}
          </Button>
        </Group>

        <Button variant="subtle" color="gray" size="compact-lg" onClick={() => navigateTo('forgotPassword')}>
          Back to Forgot Password
        </Button>
      </Stack>
    </Stack>
  )
})
